import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  type TransferSearchTransfersData,
  type UnifiedTransfer,
  transferSearchTransfers,
  transferSearchTransfersQueryKey,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { DataTable } from '@column/ui';

import { FilterDropdown, ResetFilter, Toolbar, TransferSearch } from '../Overview';
import { Loading, PopoverFilterTransferDate, PopoverFilterTransferType, PopoverFilterWrapper } from '~/components';
import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { Empty as TableEmpty, Pagination as TablePagination, tableDefaults } from '~/components/Table';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import {
  BatchTransfersSheet,
  toTransferApiQuery,
  transferTableColumns,
  useBatchSheet,
  useTransfersFilters,
} from '~/lib/transfers';
import { getAchBatchEditURL, isUnsentAchBatch } from '~/lib/transfers/utils';
import { TransferBatchTypeOptions } from '~/repositories/Transfer/TransferRepository';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useScrollStore } from '~/stores/Scroll';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

export const PageTransferBatchesOverview: React.FC = () => {
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const achEnabled = useFeatureFlag(FeatureFlag.EnableAchTransferBatches);
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('batches');
  const { addDangerNotification } = useNotificationStore();
  const { openBatchSheet } = useBatchSheet();
  const navigate = useNavigate();
  const isScrolled = (useScrollStore((s) => s.pageFrameScroll) ?? 0) > 0;

  const filters = useTransfersFilters({ defaultPageSize: pageSize });

  const batchTypeOptions: readonly string[] = achEnabled ? TransferBatchTypeOptions : ['check_batch'];
  const selectedTypes = (filters.transferTypeFilterData.type ?? [])
    .map(String)
    .filter((type) => batchTypeOptions.includes(type));

  const {
    options: bankAccountList,
    hiddenOptions: bankAccountHiddenOptions,
    search: searchBankAccounts,
  } = useBankAccountOptions({ value: filters.bankAccountIdFilterData });

  const query: TransferSearchTransfersData['query'] = {
    ...toTransferApiQuery({
      bankAccountId: filters.bankAccountIdFilterData,
      createdAtGte: filters.transferDateFilterData.createdAtGte,
      createdAtLt: filters.transferDateFilterData.createdAtLt,
    }),
    show_batches: true,
    type: (selectedTypes.length > 0 ? selectedTypes : batchTypeOptions).join(','),
    limit: pageSize,
    ...(filters.debouncedQueryInput ? { query: filters.debouncedQueryInput } : {}),
    ...(filters.cursor ? { starting_after: filters.cursor } : {}),
  };
  const { data, isFetching, isLoading } = useQuery({
    queryKey: [...transferSearchTransfersQueryKey({ query }), platformId, isSandbox],
    queryFn: async ({ signal }) => {
      const { data: response } = await transferSearchTransfers({ query, signal, throwOnError: true });
      return response;
    },
    enabled: Boolean(platformId),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const isInitialFilterRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRenderRef.current) {
      isInitialFilterRenderRef.current = false;
      return;
    }
    filters.resetPagination();
  }, [
    filters.debouncedQueryInput,
    filters.transferTypeFilterData,
    filters.bankAccountIdFilterData,
    filters.transferDateFilterData,
    filters.resetPagination,
    platformId,
    isSandbox,
  ]);

  const handleQuerySubmit = useCallback(
    (value: string) => {
      filters.cancelApplyQuery();
      filters.setQueryInput(value);
      filters.setDebouncedQueryInput(value);
    },
    [filters.cancelApplyQuery, filters.setQueryInput, filters.setDebouncedQueryInput]
  );

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [addDangerNotification, updateTableConfig]
  );

  const handlePageSizeChange = useCallback(
    (value: number) => {
      filters.setPageSize(value);
      updateTableConfig({ limit: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [addDangerNotification, filters.setPageSize, updateTableConfig]
  );

  const columns = useMemo(
    () =>
      transferTableColumns({ timeFormat, onTimeFormatChange: handleTimeFormatChange }).filter(
        (column) => column.accessor !== 'is_incoming'
      ),
    [handleTimeFormatChange, timeFormat]
  );

  const rows = useMemo(() => data?.transfers ?? [], [data]);
  const hasNext = data?.has_more ?? false;

  const handleNext = () => {
    if (rows.length === 0) return;
    filters.onNext({ cursor: rows[rows.length - 1].id });
  };

  return (
    <>
      <Toolbar $isScrolled={isScrolled}>
        <div className="flex items-center gap-2">
          <FilterDropdown
            options={bankAccountList}
            hiddenOptions={bankAccountHiddenOptions}
            size="small"
            active={filters.bankAccountIdFilterData}
            $isFilterActive={!!filters.bankAccountIdFilterData}
            label={
              bankAccountHiddenOptions?.find((b) => b.value === filters.bankAccountIdFilterData)?.label || 'Account'
            }
            search
            fullWidth
            searchLabel="Search accounts"
            onSearchChange={(description: string) => searchBankAccounts(description)}
            onChange={(value) => filters.setBankAccountIdFilterData(value)}
          />
          {achEnabled && (
            <PopoverFilterWrapper
              label="Type"
              onSubmit={(value) => filters.setTransferTypeFilterData(value)}
              isActive={selectedTypes.length > 0}
            >
              <PopoverFilterTransferType
                data={filters.transferTypeFilterData ?? {}}
                showBatchTypes
                typeOptions={batchTypeOptions}
                hideDirection
              />
            </PopoverFilterWrapper>
          )}
          <PopoverFilterWrapper
            label="Date"
            onSubmit={(value) => filters.setTransferDateFilterData(value)}
            isActive={filters.isDateFilterActive}
          >
            <PopoverFilterTransferDate data={filters.transferDateFilterData ?? {}} />
          </PopoverFilterWrapper>
          <TransferSearch
            value={filters.queryInput}
            onValueChange={filters.handleQueryInputChange}
            onFilterSubmit={handleQuerySubmit}
            placeholder="Search batches..."
            width={250}
          />
          {filters.isFilterActive && (
            <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={filters.resetAllFilters}>
              Reset
            </ResetFilter>
          )}
        </div>
      </Toolbar>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<UnifiedTransfer>
            {...tableDefaults}
            columns={columns}
            data={rows}
            isLoading={isFetching}
            emptyContent={
              <TableEmpty
                icon={<Icon.AnimationArrows />}
                title="No batches found"
                description="Batches you create or deposit will appear here."
              />
            }
            rowKey={(row) => row.id}
            onRowClick={(row) =>
              isUnsentAchBatch(row) ? navigate(getAchBatchEditURL(row.id)) : openBatchSheet(row.id)
            }
          />

          <TablePagination
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            current={filters.pageIndex + 1}
            hasPrevious={filters.hasPrevious}
            hasNext={hasNext}
            onPrevious={filters.onPrevious}
            onNext={handleNext}
          />
        </Inner>
      </Loading>
      <BatchTransfersSheet />
    </>
  );
};
